import { useEffect, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { settingsApi, type WindsorTestResult } from '../api/settings'
import { clientsApi } from '../api/clients'
import { ApiError } from '../lib/api'
import { Spinner } from '../components/Spinner'
import { DailyMetricsSettings } from '../components/panel/DailyMetricsSettings'
import {
  MODEL_OPTIONS,
  MAX_TOKENS_OPTIONS,
  EFFORT_OPTIONS,
  TOOL_STEPS_OPTIONS,
  PHASES,
  MODEL_PROFILES,
  CHAT_DEFAULTS,
} from '../lib/modelOptions'

const PLACEHOLDERS: Array<[string, string]> = [
  ['__CLIENTE__', 'Nome do cliente'],
  ['__CONTA_GOOGLE__', 'IDs Google Ads'],
  ['__CONTA_META__', 'IDs Meta Ads'],
  ['__SEMANA_INICIO__', 'Data início (DD/MM/AAAA)'],
  ['__SEMANA_FIM__', 'Data fim (DD/MM/AAAA)'],
  ['__GERENTE__', 'Nome do gerente'],
  ['__KPIS__', 'Lista de KPIs do cliente'],
  ['__DADOS_7D__', 'Dados últimos 7 dias (JSON)'],
  ['__DADOS_7D_PREV__', 'Dados 7 dias anteriores (JSON)'],
  ['__DADOS_15D__', 'Dados últimos 15 dias (JSON)'],
  ['__DADOS_30D__', 'Dados últimos 30 dias (JSON)'],
  ['__DADOS_60D__', 'Dados últimos 60 dias (JSON)'],
  ['__MERCADO__', 'Mercado do nicho — Observatório (JSON)'],
  ['__PARCELA_IMPRESSOES__', 'Parcela de impressões das campanhas de Pesquisa e PMax (tabela 7d e 30d)'],
  ['__ALTERACOES_CONTA__', 'Alterações feitas na conta do Google nos últimos 30 dias (quem, quando, antes → depois)'],
  ['__PALAVRAS_CHAVE__', 'Palavras-chave do Google que importam (30d): gasto, conversões, CPA, Índice de Qualidade e o motivo'],
  ['__CRIATIVOS_META__', 'Conjuntos e anúncios do Meta (7d × 7d anteriores): frequência, CTR, CPM e fadiga'],
  ['__PAGINAS_DESTINO__', 'Páginas de destino do Google (30d) com conversão, o que as maiores mostram (checadas todo dia) e o que o site diz do negócio'],
  ['__DADOS_GA4__', 'GA4 do site (30d; 7d × 7d anteriores): cliques do Google × sessões, conversões do Ads × eventos-chave, origens, páginas de entrada do tráfego pago e eventos-chave configurados'],
]

export default function Settings() {
  const qc = useQueryClient()
  const { data, isLoading } = useQuery({ queryKey: ['settings'], queryFn: () => settingsApi.get() })
  const settings = data?.settings ?? []
  // Clientes por perfil de modelo (para mostrar quem muda junto com cada perfil).
  const { data: clientsData } = useQuery({ queryKey: ['clients'], queryFn: () => clientsApi.list() })
  const profileClients = (profile: string) =>
    (clientsData?.clients ?? []).filter((c) => c.active && c.modelProfile === profile)

  const getSetting = (key: string) => settings.find((s) => s.key === key)

  const [promptText, setPromptText] = useState('')
  const [windsorKey, setWindsorKey] = useState('')
  const [anthropicKey, setAnthropicKey] = useState('')
  const [geminiKey, setGeminiKey] = useState('')
  const [turnstileSiteKey, setTurnstileSiteKey] = useState('')
  const [turnstileSecret, setTurnstileSecret] = useState('')
  // Observatório da Lucro Digital (NovaLD): endereço do site + token da API interna.
  const [obsUrl, setObsUrl] = useState('')
  const [obsToken, setObsToken] = useState('')
  // Config de modelo/max_tokens por fase, indexada pela chave da setting.
  const [phaseCfg, setPhaseCfg] = useState<Record<string, string>>({})
  // "Falar sobre" (chat): modelo, tokens, trava de tools e system prompt.
  const [chatModel, setChatModel] = useState<string>(CHAT_DEFAULTS.model)
  const [chatSystemPrompt, setChatSystemPrompt] = useState('')
  const [chatEffort, setChatEffort] = useState<string>(CHAT_DEFAULTS.effort)
  const [chatMaxTokens, setChatMaxTokens] = useState(String(CHAT_DEFAULTS.maxTokens))
  const [chatToolSteps, setChatToolSteps] = useState(String(CHAT_DEFAULTS.maxToolSteps))
  // Pesos de pontuação do Placar (semana/mensal).
  const [lbDay, setLbDay] = useState('12')
  const [lbFollowMaxWeek, setLbFollowMaxWeek] = useState('20')
  const [lbFollowMaxMonth, setLbFollowMaxMonth] = useState('40')
  // Peso de vitória do Placar Geral (o resto do Geral é a soma dos meses).
  const [lbAllWin, setLbAllWin] = useState('15')
  // Conhecimento: pontos por contribuição que ensina a IA (com teto por período).
  const [lbKnowledge, setLbKnowledge] = useState('2')
  const [lbKnowledgeMaxWeek, setLbKnowledgeMaxWeek] = useState('20')
  const [lbKnowledgeMaxMonth, setLbKnowledgeMaxMonth] = useState('60')
  // Contexto e memória: limites de leitura de Docs/Sheets + profundidade da memória.
  const [ctxPerFile, setCtxPerFile] = useState('10000')
  const [ctxTotal, setCtxTotal] = useState('20000')
  const [ctxSummaryWeeks, setCtxSummaryWeeks] = useState('26')
  const [ctxRawWeeks, setCtxRawWeeks] = useState('26')
  const [savedMsg, setSavedMsg] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [testResult, setTestResult] = useState<WindsorTestResult | null>(null)

  useEffect(() => {
    const p = getSetting('default_prompt')
    if (p) setPromptText(p.value)

    // Inicializa os combos com o valor salvo ou o default de cada fase.
    const cfg: Record<string, string> = {}
    for (const ph of PHASES) {
      cfg[`${ph.key}_model`] = getSetting(`${ph.key}_model`)?.value || ph.defaultModel
      cfg[`${ph.key}_max_tokens`] =
        getSetting(`${ph.key}_max_tokens`)?.value || String(ph.defaultMaxTokens)
      cfg[`${ph.key}_effort`] = getSetting(`${ph.key}_effort`)?.value || ph.defaultEffort
    }
    for (const p of MODEL_PROFILES) {
      cfg[`profile_${p.value}_model`] = getSetting(`profile_${p.value}_model`)?.value || p.defaultModel
      cfg[`profile_${p.value}_effort`] = getSetting(`profile_${p.value}_effort`)?.value || p.defaultEffort
    }
    setPhaseCfg(cfg)
    setChatModel(getSetting('chat_model')?.value || CHAT_DEFAULTS.model)
    setChatSystemPrompt(getSetting('chat_system_prompt')?.value ?? '')
    setChatEffort(getSetting('chat_effort')?.value || CHAT_DEFAULTS.effort)
    setChatMaxTokens(getSetting('chat_max_tokens')?.value || String(CHAT_DEFAULTS.maxTokens))
    setChatToolSteps(
      getSetting('chat_max_tool_steps')?.value || String(CHAT_DEFAULTS.maxToolSteps)
    )
    setTurnstileSiteKey(getSetting('turnstile_site_key')?.value ?? '')
    setObsUrl(getSetting('observatorio_api_url')?.value ?? '')
    setLbDay(getSetting('lb_points_day')?.value || '12')
    setLbFollowMaxWeek(getSetting('lb_followthrough_max_week')?.value || '20')
    setLbFollowMaxMonth(getSetting('lb_followthrough_max_month')?.value || '40')
    setLbAllWin(getSetting('lb_all_points_win')?.value || '15')
    setLbKnowledge(getSetting('lb_points_knowledge')?.value || '2')
    setLbKnowledgeMaxWeek(getSetting('lb_knowledge_max_week')?.value || '20')
    setLbKnowledgeMaxMonth(getSetting('lb_knowledge_max_month')?.value || '60')
    setCtxPerFile(getSetting('context_max_chars_per_file')?.value || '10000')
    setCtxTotal(getSetting('context_max_chars_total')?.value || '20000')
    setCtxSummaryWeeks(getSetting('context_summary_weeks')?.value || '26')
    setCtxRawWeeks(getSetting('context_raw_weeks')?.value || '26')
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data])

  const saveSettings = useMutation({
    mutationFn: (payload: Record<string, string>) => settingsApi.update(payload),
    onSuccess: (_res, payload) => {
      setSavedMsg(`Salvo: ${Object.keys(payload).join(', ')}`)
      setWindsorKey('')
      setAnthropicKey('')
      setGeminiKey('')
      setTurnstileSecret('')
      setObsToken('')
      qc.invalidateQueries({ queryKey: ['settings'] })
    },
    onError: (err) => setError(err instanceof ApiError ? err.message : 'Erro ao salvar'),
  })

  const testWindsor = useMutation({
    mutationFn: () => settingsApi.testWindsor({}),
    onSuccess: (res) => setTestResult(res),
    onError: (err) =>
      setTestResult({ ok: false, error: err instanceof ApiError ? err.message : 'Falha no teste' }),
  })

  if (isLoading) return <Spinner />

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-bold text-slate-900">Configurações</h1>

      {savedMsg && (
        <p className="rounded-md bg-green-50 px-3 py-2 text-sm text-green-700">{savedMsg}</p>
      )}
      {error && <p className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">{error}</p>}

      {/* Prompt padrão */}
      <section className="rounded-xl border border-slate-200 bg-white p-5">
        <h2 className="mb-3 text-base font-semibold text-slate-900">Prompt padrão (fase 1)</h2>
        <div className="grid gap-4 lg:grid-cols-[1fr_240px]">
          <textarea
            value={promptText}
            onChange={(e) => setPromptText(e.target.value)}
            rows={18}
            className="w-full rounded-md border border-slate-300 px-3 py-2 font-mono text-xs focus:border-brand-500 focus:outline-none"
          />
          <aside className="rounded-lg border border-slate-200 bg-slate-50 p-3">
            <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
              Placeholders disponíveis
            </h3>
            <ul className="space-y-1.5">
              {PLACEHOLDERS.map(([token, desc]) => (
                <li key={token} className="text-xs">
                  <code className="font-mono text-brand-700">{token}</code>
                  <span className="block text-slate-500">{desc}</span>
                </li>
              ))}
            </ul>
          </aside>
        </div>
        <button
          onClick={() => {
            setError(null)
            setSavedMsg(null)
            saveSettings.mutate({ default_prompt: promptText })
          }}
          disabled={saveSettings.isPending}
          className="mt-3 inline-flex items-center gap-2 rounded-md bg-brand-600 px-4 py-2 text-sm font-semibold text-white hover:bg-brand-700 disabled:opacity-50"
        >
          {saveSettings.isPending && <Spinner className="h-4 w-4 text-white" />}
          Salvar prompt
        </button>
      </section>

      {/* Modelos por fase */}
      <section className="rounded-xl border border-slate-200 bg-white p-5">
        <h2 className="mb-1 text-base font-semibold text-slate-900">Modelos por fase</h2>
        <p className="mb-4 text-xs text-slate-500">
          Escolha o modelo, o limite de tokens de saída (max_tokens) e o <strong>raciocínio</strong>{' '}
          (esforço de thinking) de cada fase. O modelo define o provider automaticamente (Claude ou
          Gemini). O raciocínio afeta os modelos <strong>Claude</strong> e o{' '}
          <strong>Gemini 3.8 Flash</strong> — nos outros Gemini é ignorado.
          Com raciocínio ligado, a chamada usa streaming e reserva tokens de saída suficientes para
          o modelo pensar e ainda fechar o JSON sem truncar. Sonnet 4.6, Opus 4.6 e Haiku 4.5 têm
          teto duro de raciocínio (o esforço define esse teto); Opus 5/5.5, Sonnet 5/5.5 e Fable 5
          se auto-regulam pelo esforço (nos 5.5 os níveis foram recalibrados: vale testar Médio
          antes de subir). No Gemini 3.8 Flash o raciocínio conta dentro do teto de saída, então o teto
          sobe para 64k e o esforço vira o nível de raciocínio (Baixo, Médio ou Alto; Muito alto e
          Máximo contam como Alto). Sem configuração, usa Claude Sonnet 4.6 com esforço Alto. Na
          Fase 1 o modelo e o raciocínio vêm do <strong>perfil</strong> de cada cliente (abaixo).
        </p>
        <div className="space-y-3">
          {PHASES.map((ph) => (
            <div
              key={ph.key}
              className="grid items-center gap-3 sm:grid-cols-[1fr_180px_130px_130px]"
            >
              <label className="text-sm font-medium text-slate-700">{ph.label}</label>
              {ph.key === 'phase1' ? (
                <span className="px-1 text-xs text-slate-500">Modelo do perfil do cliente ↓</span>
              ) : (
              <select
                value={phaseCfg[`${ph.key}_model`] ?? ph.defaultModel}
                onChange={(e) =>
                  setPhaseCfg((c) => ({ ...c, [`${ph.key}_model`]: e.target.value }))
                }
                className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none"
              >
                {MODEL_OPTIONS.map((m) => (
                  <option key={m.value} value={m.value}>
                    {m.label}
                  </option>
                ))}
              </select>
              )}
              <select
                value={phaseCfg[`${ph.key}_max_tokens`] ?? String(ph.defaultMaxTokens)}
                onChange={(e) =>
                  setPhaseCfg((c) => ({ ...c, [`${ph.key}_max_tokens`]: e.target.value }))
                }
                className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none"
              >
                {MAX_TOKENS_OPTIONS.map((t) => (
                  <option key={t} value={String(t)}>
                    {t.toLocaleString('pt-BR')} tokens
                  </option>
                ))}
              </select>
              {ph.key === 'phase1' ? (
                <span className="px-1 text-xs text-slate-500">do perfil ↓</span>
              ) : (
              <select
                value={phaseCfg[`${ph.key}_effort`] ?? ph.defaultEffort}
                onChange={(e) =>
                  setPhaseCfg((c) => ({ ...c, [`${ph.key}_effort`]: e.target.value }))
                }
                title="Raciocínio (thinking) — afeta modelos Claude e o Gemini 3.8 Flash"
                className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none"
              >
                {EFFORT_OPTIONS.map((ef) => (
                  <option key={ef.value} value={ef.value}>
                    {ef.label}
                  </option>
                ))}
              </select>
              )}
            </div>
          ))}
        </div>

        {/* Perfis da fase 1: o cliente escolhe o perfil; o modelo de cada perfil fica aqui. */}
        <div className="mt-5 rounded-lg border border-slate-200 bg-slate-50 p-4">
          <h3 className="text-sm font-semibold text-slate-900">Perfis da análise semanal (Fase 1)</h3>
          <p className="mb-3 text-xs text-slate-500">
            Cada cliente usa um perfil (no cadastro do cliente). Mudar o modelo ou o raciocínio de um
            perfil muda todos os clientes daquele perfil. O limite de tokens é o da Fase 1.
          </p>
          <div className="space-y-3">
            {MODEL_PROFILES.map((p) => {
              const members = profileClients(p.value)
              return (
                <div
                  key={p.value}
                  className="grid items-center gap-3 sm:grid-cols-[1fr_180px_130px_130px]"
                >
                  <div>
                    <span className="text-sm font-medium text-slate-700">{p.label}</span>
                    <span
                      className="block text-xs text-slate-500"
                      title={members.map((c) => c.name).join(', ') || undefined}
                    >
                      {members.length === 1 ? '1 cliente ativo' : `${members.length} clientes ativos`}
                    </span>
                  </div>
                  <select
                    value={phaseCfg[`profile_${p.value}_model`] ?? p.defaultModel}
                    onChange={(e) =>
                      setPhaseCfg((c) => ({ ...c, [`profile_${p.value}_model`]: e.target.value }))
                    }
                    className="w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm focus:border-brand-500 focus:outline-none"
                  >
                    {MODEL_OPTIONS.map((m) => (
                      <option key={m.value} value={m.value}>
                        {m.label}
                      </option>
                    ))}
                  </select>
                  <span />
                  <select
                    value={phaseCfg[`profile_${p.value}_effort`] ?? p.defaultEffort}
                    onChange={(e) =>
                      setPhaseCfg((c) => ({ ...c, [`profile_${p.value}_effort`]: e.target.value }))
                    }
                    title="Raciocínio (thinking) do perfil"
                    className="w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm focus:border-brand-500 focus:outline-none"
                  >
                    {EFFORT_OPTIONS.map((ef) => (
                      <option key={ef.value} value={ef.value}>
                        {ef.label}
                      </option>
                    ))}
                  </select>
                </div>
              )
            })}
          </div>
        </div>

        <button
          onClick={() => {
            setError(null)
            setSavedMsg(null)
            saveSettings.mutate(phaseCfg)
          }}
          disabled={saveSettings.isPending}
          className="mt-4 inline-flex items-center gap-2 rounded-md bg-brand-600 px-4 py-2 text-sm font-semibold text-white hover:bg-brand-700 disabled:opacity-50"
        >
          {saveSettings.isPending && <Spinner className="h-4 w-4 text-white" />}
          Salvar modelos
        </button>
      </section>

      {/* Falar sobre (chat) */}
      <section className="rounded-xl border border-slate-200 bg-white p-5">
        <h2 className="mb-1 text-base font-semibold text-slate-900">Falar sobre (chat)</h2>
        <p className="mb-4 text-xs text-slate-500">
          Tela em Clientes → Falar sobre. O gerente conversa com a IA sobre os relatórios e KPIs de um
          cliente; a IA usa ferramentas para buscar esses dados. Configure o modelo, o limite de
          tokens da resposta, a trava do loop de ferramentas e o system prompt.
        </p>
        <div className="space-y-4">
          <div>
            <label className="mb-1 block text-sm font-medium text-slate-700">Modelo</label>
            <select
              value={chatModel}
              onChange={(e) => setChatModel(e.target.value)}
              className="w-full max-w-xs rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none"
            >
              {MODEL_OPTIONS.map((m) => (
                <option key={m.value} value={m.value}>
                  {m.label}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-slate-700">Raciocínio</label>
            <select
              value={chatEffort}
              onChange={(e) => setChatEffort(e.target.value)}
              className="w-full max-w-xs rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none"
            >
              {EFFORT_OPTIONS.map((ef) => (
                <option key={ef.value} value={ef.value}>
                  {ef.label}
                </option>
              ))}
            </select>
            <p className="mt-1 text-xs text-slate-400">
              Esforço de thinking — afeta se o chat usar um modelo <strong>Claude</strong> ou o{' '}
              <strong>Gemini 3.8 Flash</strong> (Baixo responde mais rápido). Nos outros Gemini é
              ignorado.
            </p>
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-slate-700">
              Máx. de tokens da resposta
            </label>
            <select
              value={chatMaxTokens}
              onChange={(e) => setChatMaxTokens(e.target.value)}
              className="w-full max-w-xs rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none"
            >
              {MAX_TOKENS_OPTIONS.map((t) => (
                <option key={t} value={String(t)}>
                  {t.toLocaleString('pt-BR')} tokens
                </option>
              ))}
            </select>
            <p className="mt-1 text-xs text-slate-400">
              Teto de saída por resposta. No <strong>Gemini</strong> os tokens de raciocínio contam
              dentro desse teto — se ficar baixo, a resposta sai cortada (ou vazia). No Gemini 3.8
              Flash e nos modelos Claude com raciocínio o teto real é maior: Gemini 3.8 Flash e Opus
              5/Fable 5 sobem para 64k e Sonnet 4.6/Haiku 4.5 somam uma reserva fixa de saída.
            </p>
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-slate-700">
              Trava de chamadas de ferramentas
            </label>
            <select
              value={chatToolSteps}
              onChange={(e) => setChatToolSteps(e.target.value)}
              className="w-full max-w-xs rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none"
            >
              {TOOL_STEPS_OPTIONS.map((n) => (
                <option key={n} value={String(n)}>
                  {n} passos
                </option>
              ))}
            </select>
            <p className="mt-1 text-xs text-slate-400">
              Máximo de rodadas de modelo por resposta enquanto a IA busca dados com as ferramentas.
              Ao estourar, a conversa é interrompida sem resposta — suba se as perguntas exigirem
              muitas consultas.
            </p>
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-slate-700">System prompt</label>
            <textarea
              value={chatSystemPrompt}
              onChange={(e) => setChatSystemPrompt(e.target.value)}
              rows={8}
              placeholder="Deixe em branco para usar o padrão. O contexto do negócio e o nome do cliente são adicionados automaticamente."
              className="w-full rounded-md border border-slate-300 px-3 py-2 font-mono text-xs focus:border-brand-500 focus:outline-none"
            />
            <p className="mt-1 text-xs text-slate-400">
              O contexto do negócio do cliente e a instrução de que as ferramentas já trazem dados só
              daquele cliente são anexados automaticamente ao system prompt.
            </p>
          </div>
        </div>
        <button
          onClick={() => {
            setError(null)
            setSavedMsg(null)
            saveSettings.mutate({
              chat_model: chatModel,
              chat_system_prompt: chatSystemPrompt,
              chat_effort: chatEffort,
              chat_max_tokens: chatMaxTokens,
              chat_max_tool_steps: chatToolSteps,
            })
          }}
          disabled={saveSettings.isPending}
          className="mt-4 inline-flex items-center gap-2 rounded-md bg-brand-600 px-4 py-2 text-sm font-semibold text-white hover:bg-brand-700 disabled:opacity-50"
        >
          {saveSettings.isPending && <Spinner className="h-4 w-4 text-white" />}
          Salvar chat
        </button>
      </section>

      {/* Placar — pesos de pontuação */}
      <section className="rounded-xl border border-slate-200 bg-white p-5">
        <h2 className="mb-1 text-base font-semibold text-slate-900">Placar — pontuação</h2>
        <p className="mb-4 text-xs text-slate-500">
          Semana/Mensal = dias ativos × (peso do dia) + bônus de follow-through (proporcional ao % de
          ações resolvidas dos seus clientes, até o máximo do período). O <strong>Geral</strong> usa o
          segundo grupo.
        </p>

        <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
          Semana e Mensal
        </h3>
        <div className="grid gap-4 sm:grid-cols-3">
          <NumField label="Pontos por dia ativo (×)" value={lbDay} onChange={setLbDay} />
          <NumField
            label="Bônus máx. follow-through — Semana (+)"
            value={lbFollowMaxWeek}
            onChange={setLbFollowMaxWeek}
          />
          <NumField
            label="Bônus máx. follow-through — Mês (+)"
            value={lbFollowMaxMonth}
            onChange={setLbFollowMaxMonth}
          />
        </div>

        <h3 className="mb-2 mt-5 text-xs font-semibold uppercase tracking-wide text-slate-500">
          Conhecimento (ensinar a IA)
        </h3>
        <p className="mb-2 text-xs text-slate-400">
          Cada resposta a uma pergunta da IA, aprendizado confirmado/corrigido, fato escrito, motivo ou
          comentário numa ação e investigação respondida vale pontos — até o teto do período.
        </p>
        <div className="grid gap-4 sm:grid-cols-3">
          <NumField label="Pontos por contribuição (×)" value={lbKnowledge} onChange={setLbKnowledge} />
          <NumField label="Teto — Semana (+)" value={lbKnowledgeMaxWeek} onChange={setLbKnowledgeMaxWeek} />
          <NumField label="Teto — Mês (+)" value={lbKnowledgeMaxMonth} onChange={setLbKnowledgeMaxMonth} />
        </div>

        <h3 className="mb-2 mt-5 text-xs font-semibold uppercase tracking-wide text-slate-500">
          Geral
        </h3>
        <p className="mb-2 text-xs text-slate-400">
          O Geral <strong>soma as pontuações mensais</strong> (dias ativos + follow-through, mesma
          fórmula do Mês, incluindo o mês corrente). Só o peso da vitória é próprio do Geral.
        </p>
        <div className="grid gap-4 sm:grid-cols-3">
          <NumField label="Pontos por vitória (×)" value={lbAllWin} onChange={setLbAllWin} />
        </div>

        <button
          onClick={() => {
            setError(null)
            setSavedMsg(null)
            const clamp = (v: string) => String(Math.max(0, Math.min(1000, Math.round(Number(v) || 0))))
            saveSettings.mutate({
              lb_points_day: clamp(lbDay),
              lb_followthrough_max_week: clamp(lbFollowMaxWeek),
              lb_followthrough_max_month: clamp(lbFollowMaxMonth),
              lb_all_points_win: clamp(lbAllWin),
              lb_points_knowledge: clamp(lbKnowledge),
              lb_knowledge_max_week: clamp(lbKnowledgeMaxWeek),
              lb_knowledge_max_month: clamp(lbKnowledgeMaxMonth),
            })
          }}
          disabled={saveSettings.isPending}
          className="mt-5 inline-flex items-center gap-2 rounded-md bg-brand-600 px-4 py-2 text-sm font-semibold text-white hover:bg-brand-700 disabled:opacity-50"
        >
          {saveSettings.isPending && <Spinner className="h-4 w-4 text-white" />}
          Salvar pontuação
        </button>
      </section>

      {/* Contexto e memória */}
      <section className="rounded-xl border border-slate-200 bg-white p-5">
        <h2 className="mb-1 text-base font-semibold text-slate-900">Contexto e memória</h2>
        <p className="mb-4 text-xs text-slate-500">
          O <strong>Contexto do negócio</strong> de cada cliente pode conter links de{' '}
          <strong>Google Docs e Google Sheets públicos</strong> ("qualquer pessoa com o link"): o
          conteúdo é lido e injetado na análise. Os limites abaixo evitam estourar o prompt. A{' '}
          <strong>memória</strong> define quantas semanas anteriores entram na análise (o override
          por cliente, quando definido, tem prioridade).
        </p>

        <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
          Leitura de documentos (Docs/Sheets)
        </h3>
        <div className="grid gap-4 sm:grid-cols-2">
          <NumField
            label="Máx. de caracteres por arquivo"
            value={ctxPerFile}
            onChange={setCtxPerFile}
            min={0}
            max={1000000}
            hint="Cada documento é truncado neste tamanho. Sheets: só a 1ª aba."
          />
          <NumField
            label="Máx. de caracteres somando todos os arquivos"
            value={ctxTotal}
            onChange={setCtxTotal}
            min={0}
            max={1000000}
            hint="Teto total do conteúdo lido por análise (todos os links juntos)."
          />
        </div>

        <h3 className="mb-2 mt-5 text-xs font-semibold uppercase tracking-wide text-slate-500">
          Profundidade da memória (semanas)
        </h3>
        <div className="grid gap-4 sm:grid-cols-2">
          <NumField
            label="Semanas de resumo da análise injetadas"
            value={ctxSummaryWeeks}
            onChange={setCtxSummaryWeeks}
            min={1}
            max={52}
            hint="Resumos compactos das semanas anteriores (memória de longo prazo)."
          />
          <NumField
            label="Semanas de ações + feedback injetadas"
            value={ctxRawWeeks}
            onChange={setCtxRawWeeks}
            min={1}
            max={52}
            hint="Ações recomendadas e o status/comentário do gerente (anti-repetição)."
          />
        </div>

        <button
          onClick={() => {
            setError(null)
            setSavedMsg(null)
            const clampChars = (v: string) =>
              String(Math.max(0, Math.min(1000000, Math.round(Number(v) || 0))))
            const clampWeeks = (v: string) =>
              String(Math.max(1, Math.min(52, Math.round(Number(v) || 1))))
            saveSettings.mutate({
              context_max_chars_per_file: clampChars(ctxPerFile),
              context_max_chars_total: clampChars(ctxTotal),
              context_summary_weeks: clampWeeks(ctxSummaryWeeks),
              context_raw_weeks: clampWeeks(ctxRawWeeks),
            })
          }}
          disabled={saveSettings.isPending}
          className="mt-5 inline-flex items-center gap-2 rounded-md bg-brand-600 px-4 py-2 text-sm font-semibold text-white hover:bg-brand-700 disabled:opacity-50"
        >
          {saveSettings.isPending && <Spinner className="h-4 w-4 text-white" />}
          Salvar contexto e memória
        </button>
      </section>

      {/* Chaves de API */}
      <section className="rounded-xl border border-slate-200 bg-white p-5">
        <h2 className="mb-3 text-base font-semibold text-slate-900">Chaves de API</h2>
        <p className="mb-3 text-xs text-slate-500">
          As chaves são mascaradas. Deixe em branco para manter a atual; preencha para substituir. As
          variáveis de ambiente (WINDSOR_API_KEY / ANTHROPIC_API_KEY) têm prioridade sobre estes
          valores.
        </p>
        <div className="grid gap-4 sm:grid-cols-2">
          <ApiKeyField
            label="Windsor API Key"
            current={getSetting('windsor_api_key')?.value ?? ''}
            isSet={getSetting('windsor_api_key')?.isSet ?? false}
            value={windsorKey}
            onChange={setWindsorKey}
          />
          <ApiKeyField
            label="Anthropic API Key"
            current={getSetting('anthropic_api_key')?.value ?? ''}
            isSet={getSetting('anthropic_api_key')?.isSet ?? false}
            value={anthropicKey}
            onChange={setAnthropicKey}
          />
          <ApiKeyField
            label="Gemini API Key"
            current={getSetting('gemini_api_key')?.value ?? ''}
            isSet={getSetting('gemini_api_key')?.isSet ?? false}
            value={geminiKey}
            onChange={setGeminiKey}
          />
        </div>
        <button
          onClick={() => {
            setError(null)
            setSavedMsg(null)
            const payload: Record<string, string> = {}
            if (windsorKey.trim()) payload.windsor_api_key = windsorKey.trim()
            if (anthropicKey.trim()) payload.anthropic_api_key = anthropicKey.trim()
            if (geminiKey.trim()) payload.gemini_api_key = geminiKey.trim()
            if (Object.keys(payload).length === 0) {
              setError('Nenhuma chave preenchida')
              return
            }
            saveSettings.mutate(payload)
          }}
          disabled={saveSettings.isPending}
          className="mt-3 inline-flex items-center gap-2 rounded-md bg-brand-600 px-4 py-2 text-sm font-semibold text-white hover:bg-brand-700 disabled:opacity-50"
        >
          Salvar chaves
        </button>
      </section>

      {/* Observatório da Lucro Digital (NovaLD) */}
      <section className="rounded-xl border border-slate-200 bg-white p-5">
        <h2 className="mb-1 text-base font-semibold text-slate-900">Observatório Lucro Digital</h2>
        <p className="mb-3 text-xs text-slate-500">
          Referência de mercado do nicho de cada cliente (demanda, previsão, CPC e lance do Planejador do
          Google), escolhido no cadastro do cliente. A demanda e o CPC atual vêm do pacote público; o{' '}
          <strong>token</strong> libera o catálogo de nichos, o pedido de nicho novo, a tendência do CPC,
          as buscas do nicho (que definem os termos core) e as negativas validadas. É o mesmo valor da
          variável <code className="font-mono">OBSERVATORIO_API_TOKEN</code> do serviço Simuladores do
          NovaLD.
        </p>
        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <label className="mb-1 block text-sm font-medium text-slate-700">Endereço do site</label>
            <input
              value={obsUrl}
              onChange={(e) => setObsUrl(e.target.value)}
              placeholder="https://www.lucrodigital.com.br (padrão)"
              className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none"
            />
          </div>
          <ApiKeyField
            label="Token da API"
            current={getSetting('observatorio_api_token')?.value ?? ''}
            isSet={getSetting('observatorio_api_token')?.isSet ?? false}
            value={obsToken}
            onChange={setObsToken}
          />
        </div>
        <button
          onClick={() => {
            setError(null)
            setSavedMsg(null)
            const payload: Record<string, string> = { observatorio_api_url: obsUrl.trim() }
            if (obsToken.trim()) payload.observatorio_api_token = obsToken.trim()
            saveSettings.mutate(payload)
          }}
          disabled={saveSettings.isPending}
          className="mt-3 inline-flex items-center gap-2 rounded-md bg-brand-600 px-4 py-2 text-sm font-semibold text-white hover:bg-brand-700 disabled:opacity-50"
        >
          Salvar Observatório
        </button>
      </section>

      {/* Cloudflare Turnstile (captcha do login) */}
      <section className="rounded-xl border border-slate-200 bg-white p-5">
        <h2 className="mb-1 text-base font-semibold text-slate-900">
          Segurança — Cloudflare Turnstile (login)
        </h2>
        <p className="mb-3 text-xs text-slate-500">
          Captcha na tela de login. Preencha as duas chaves para ativar; deixe a Site Key em branco
          para desativar. Cada ambiente (Dev/Prod) usa as suas próprias chaves.
        </p>
        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <label className="mb-1 block text-sm font-medium text-slate-700">Site Key (pública)</label>
            <input
              value={turnstileSiteKey}
              onChange={(e) => setTurnstileSiteKey(e.target.value)}
              placeholder="0x4AAAAAAA..."
              className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none"
            />
          </div>
          <ApiKeyField
            label="Secret Key"
            current={getSetting('turnstile_secret_key')?.value ?? ''}
            isSet={getSetting('turnstile_secret_key')?.isSet ?? false}
            value={turnstileSecret}
            onChange={setTurnstileSecret}
          />
        </div>
        <button
          onClick={() => {
            setError(null)
            setSavedMsg(null)
            const payload: Record<string, string> = { turnstile_site_key: turnstileSiteKey.trim() }
            if (turnstileSecret.trim()) payload.turnstile_secret_key = turnstileSecret.trim()
            saveSettings.mutate(payload)
          }}
          disabled={saveSettings.isPending}
          className="mt-3 inline-flex items-center gap-2 rounded-md bg-brand-600 px-4 py-2 text-sm font-semibold text-white hover:bg-brand-700 disabled:opacity-50"
        >
          Salvar Turnstile
        </button>
      </section>

      <DailyMetricsSettings />

      {/* Teste Windsor */}
      <section className="rounded-xl border border-slate-200 bg-white p-5">
        <h2 className="mb-1 text-base font-semibold text-slate-900">Testar Windsor</h2>
        <p className="mb-3 text-xs text-slate-500">
          Faz um GET no conector <code className="font-mono">all</code> do Windsor.ai pedindo os
          campos <code className="font-mono">date, source, spend</code> dos últimos 7 dias, usando a
          chave configurada. Abaixo aparece a URL chamada (chave mascarada) e a resposta.
        </p>
        <button
          onClick={() => {
            setTestResult(null)
            testWindsor.mutate()
          }}
          disabled={testWindsor.isPending}
          className="inline-flex items-center gap-2 rounded-md border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50"
        >
          {testWindsor.isPending && <Spinner className="h-4 w-4" />}
          Testar conexão (last_7d)
        </button>

        {testResult && (
          <div className="mt-3 space-y-3">
            <div
              className={`rounded-md px-3 py-2 text-sm ${
                testResult.ok ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'
              }`}
            >
              {testResult.ok
                ? `OK — ${testResult.rowCount} linha(s) retornada(s).`
                : `Falha: ${testResult.error}`}
            </div>

            {testResult.request && (
              <div>
                <h3 className="mb-1 text-xs font-semibold uppercase tracking-wide text-slate-500">
                  Requisição enviada
                </h3>
                <pre className="overflow-x-auto rounded-md bg-slate-900 px-3 py-2 text-xs text-slate-100">
                  <span className="text-slate-400">GET</span> {testResult.request.url}
                </pre>
              </div>
            )}

            <div>
              <h3 className="mb-1 text-xs font-semibold uppercase tracking-wide text-slate-500">
                Resposta {testResult.ok ? '(amostra — até 3 linhas)' : '(erro)'}
              </h3>
              <pre className="max-h-72 overflow-auto rounded-md bg-slate-50 px-3 py-2 text-xs text-slate-700">
                {JSON.stringify(
                  testResult.ok
                    ? { rowCount: testResult.rowCount, sample: testResult.sample }
                    : { error: testResult.error },
                  null,
                  2
                )}
              </pre>
            </div>
          </div>
        )}
      </section>
    </div>
  )
}

function NumField({
  label,
  value,
  onChange,
  min = 0,
  max = 1000,
  hint,
}: {
  label: string
  value: string
  onChange: (v: string) => void
  min?: number
  max?: number
  hint?: string
}) {
  return (
    <div>
      <label className="mb-1 block text-sm font-medium text-slate-700">{label}</label>
      <input
        type="number"
        min={min}
        max={max}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none"
      />
      {hint && <p className="mt-1 text-xs text-slate-400">{hint}</p>}
    </div>
  )
}

function ApiKeyField({
  label,
  current,
  isSet,
  value,
  onChange,
}: {
  label: string
  current: string
  isSet: boolean
  value: string
  onChange: (v: string) => void
}) {
  return (
    <div>
      <label className="mb-1 block text-sm font-medium text-slate-700">{label}</label>
      <input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={isSet ? `Atual: ${current}` : 'Não configurada'}
        className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none"
      />
    </div>
  )
}
